import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, RefreshCw, Plus, Trash2,
  ShieldCheck, Users, TrendingUp, Waves,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency as fmtCur } from '@/lib/market';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DONUT_COLORS = ['#ffd87a', '#e8a838', '#c97a3a', '#dae4c4', '#c8d9b0', '#a86830', '#eae9da', '#d9d9d9'];

const HOLDER_TYPE_LABELS = { founder: 'Founder', investor: 'Investor', employee: 'Employee', option_pool: 'Option Pool' };
const SECURITY_TYPE_LABELS = { common: 'Common', preferred: 'Preferred', options: 'Options', warrants: 'Warrants', safe: 'SAFE', convertible: 'Convertible' };
const INVESTMENT_TYPE_LABELS = { equity: 'Equity', safe: 'SAFE', convertible_note: 'Convertible Note', warrant: 'Warrant', secondary: 'Secondary' };

// ── Donut Chart (adapted from ConsolidatedDashboard.js) ─────────────────────
const DonutChart = ({ data, size = 180 }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;
  let angle = -90;
  const r = size / 2 - 14, cx = size / 2, cy = size / 2, ir = r * 0.52;
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="cursor-pointer" onMouseLeave={() => setHovered(null)} onMouseMove={handleMouseMove}>
        {data.map((item, i) => {
          const pct = item.value / total, deg = pct * 360;
          const s = angle, e2 = angle + deg; angle = e2;
          const sr = s * Math.PI / 180, er = e2 * Math.PI / 180;
          const d = [
            `M ${cx + r * Math.cos(sr)} ${cy + r * Math.sin(sr)}`,
            `A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${cx + r * Math.cos(er)} ${cy + r * Math.sin(er)}`,
            `L ${cx + ir * Math.cos(er)} ${cy + ir * Math.sin(er)}`,
            `A ${ir} ${ir} 0 ${deg > 180 ? 1 : 0} 0 ${cx + ir * Math.cos(sr)} ${cy + ir * Math.sin(sr)}`,
            'Z',
          ].join(' ');
          return <path key={i} d={d} fill={item.color} stroke="none"
            style={{ opacity: hovered == null || hovered === i ? 1 : 0.35, transition: 'opacity 0.15s' }}
            onMouseEnter={() => setHovered(i)} />;
        })}
      </svg>
      {hovered != null && data[hovered] && (
        <div className="absolute z-50 pointer-events-none bg-white border border-[#e5e5e5] text-xs px-2.5 py-1.5 whitespace-nowrap rounded-lg shadow-md"
          style={{ left: tooltipPos.x + 10, top: tooltipPos.y - 38, transform: tooltipPos.x > size * 0.6 ? 'translateX(-100%)' : 'none' }}>
          <span className="font-medium text-[#c97a3a]">{data[hovered].name}</span>
          <span className="ml-2">{((data[hovered].value / total) * 100).toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};

function emptyEntryRow() {
  return { holder_name: '', holder_type: 'investor', security_type: 'common', shares: '', ownership_percentage: '', fully_diluted_percentage: '', notes: '' };
}

export default function StartupDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const fmt = (v) => (v == null ? '—' : fmtCur(v, user?.market));

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [valuations, setValuations] = useState([]);
  const [capTableHistory, setCapTableHistory] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);

  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotForm, setSnapshotForm] = useState({ snapshot_date: '', total_shares_outstanding: '', fully_diluted_shares: '', source: '', entries: [emptyEntryRow()] });
  const [savingSnapshot, setSavingSnapshot] = useState(false);

  const [safeDialogOpen, setSafeDialogOpen] = useState(false);
  const [safeForm, setSafeForm] = useState({ round_price_per_share: '', pre_money_valuation: '', fully_diluted_shares_pre_round: '' });
  const [convertingSafes, setConvertingSafes] = useState(false);

  const [waterfallDialogOpen, setWaterfallDialogOpen] = useState(false);
  const [exitValue, setExitValue] = useState('');
  const [waterfallResult, setWaterfallResult] = useState(null);
  const [runningWaterfall, setRunningWaterfall] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, valuationsRes, historyRes] = await Promise.all([
        axios.get(`${API}/startups/companies/${id}/summary`),
        axios.get(`${API}/startups/valuations/${id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/startups/cap-table/${id}/history`).catch(() => ({ data: [] })),
      ]);
      setSummary(summaryRes.data);
      setValuations(valuationsRes.data || []);
      const history = historyRes.data || [];
      setCapTableHistory(history);
      const current = history.find(s => s.is_current) || history[0];
      setSelectedSnapshotId(current?.id || null);
    } catch {
      toast.error('Failed to load company details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-6 text-center py-16">
        <Building2 className="w-10 h-10 text-[#4a4a4a] mx-auto mb-3" />
        <p className="text-sm text-[#4a4a4a]">Company not found</p>
        <Link to="/startups" className="text-xs text-[#c97a3a] mt-2 inline-block">Back to Portfolio Companies</Link>
      </div>
    );
  }

  const { company, investments, current_valuation: currentValuation, summary: stats } = summary;
  const selectedSnapshot = capTableHistory.find(s => s.id === selectedSnapshotId) || null;
  const entries = selectedSnapshot?.entries || [];

  const donutData = Object.entries(
    entries.reduce((acc, e) => {
      const key = e.holder_type || 'other';
      acc[key] = (acc[key] || 0) + (e.shares || 0);
      return acc;
    }, {})
  ).map(([type, value], i) => ({ name: HOLDER_TYPE_LABELS[type] || type, value, color: DONUT_COLORS[i % DONUT_COLORS.length] }));

  // ── Snapshot form helpers ──────────────────────────────────────────────
  const openSnapshotDialog = () => {
    setSnapshotForm({
      snapshot_date: new Date().toISOString().slice(0, 10),
      total_shares_outstanding: selectedSnapshot?.total_shares_outstanding || '',
      fully_diluted_shares: selectedSnapshot?.fully_diluted_shares || '',
      source: '',
      entries: entries.length ? entries.map(e => ({ ...e })) : [emptyEntryRow()],
    });
    setSnapshotDialogOpen(true);
  };

  const updateEntryRow = (idx, field, value) => {
    setSnapshotForm(f => ({ ...f, entries: f.entries.map((row, i) => i === idx ? { ...row, [field]: value } : row) }));
  };

  const addEntryRow = () => setSnapshotForm(f => ({ ...f, entries: [...f.entries, emptyEntryRow()] }));
  const removeEntryRow = (idx) => setSnapshotForm(f => ({ ...f, entries: f.entries.filter((_, i) => i !== idx) }));

  const submitSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const payload = {
        startup_id: id,
        snapshot_date: snapshotForm.snapshot_date,
        total_shares_outstanding: parseFloat(snapshotForm.total_shares_outstanding) || 0,
        fully_diluted_shares: parseFloat(snapshotForm.fully_diluted_shares) || 0,
        source: snapshotForm.source || null,
        entries: snapshotForm.entries.map(row => ({
          holder_name: row.holder_name,
          holder_type: row.holder_type,
          security_type: row.security_type,
          shares: parseFloat(row.shares) || 0,
          ownership_percentage: parseFloat(row.ownership_percentage) || 0,
          fully_diluted_percentage: parseFloat(row.fully_diluted_percentage) || 0,
          notes: row.notes || null,
        })),
      };
      await axios.post(`${API}/startups/cap-table`, payload);
      toast.success('Cap table snapshot saved');
      setSnapshotDialogOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save snapshot');
    } finally {
      setSavingSnapshot(false);
    }
  };

  // ── SAFE conversion ─────────────────────────────────────────────────────
  const runConvertSafes = async () => {
    setConvertingSafes(true);
    try {
      const res = await axios.post(`${API}/startups/${id}/cap-table/convert-safes`, {
        round_price_per_share: parseFloat(safeForm.round_price_per_share) || 0,
        pre_money_valuation: parseFloat(safeForm.pre_money_valuation) || 0,
        fully_diluted_shares_pre_round: parseFloat(safeForm.fully_diluted_shares_pre_round) || 0,
      });
      const conversions = res.data?.conversions || [];
      if (conversions.length === 0) {
        toast.info('No active SAFE / convertible-note investments to convert');
      } else {
        const entityNames = Object.fromEntries(investments.map(i => [i.entity_id, i.entity_id]));
        setSnapshotForm(f => ({
          ...f,
          entries: [
            ...f.entries.filter(r => r.holder_name),
            ...conversions.map(c => ({
              holder_name: entityNames[c.entity_id] || c.entity_id || '',
              holder_type: 'investor',
              security_type: 'convertible',
              shares: c.converted_shares?.toFixed(2) || '',
              ownership_percentage: '',
              fully_diluted_percentage: '',
              notes: `Converted @ ${fmt(c.effective_price_per_share)}/sh`,
            })),
          ],
        }));
        toast.success(`Converted ${conversions.length} SAFE/note position(s) — review rows in the snapshot form`);
      }
      setSafeDialogOpen(false);
      if (!snapshotDialogOpen) openSnapshotDialog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert SAFEs');
    } finally {
      setConvertingSafes(false);
    }
  };

  // ── Waterfall ────────────────────────────────────────────────────────────
  const runWaterfall = async () => {
    setRunningWaterfall(true);
    setWaterfallResult(null);
    try {
      const res = await axios.post(`${API}/startups/${id}/cap-table/waterfall`, { exit_value: parseFloat(exitValue) || 0 });
      setWaterfallResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to run waterfall');
    } finally {
      setRunningWaterfall(false);
    }
  };

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <Link to="/startups" className="inline-flex items-center gap-1.5 text-xs text-[#4a4a4a] hover:text-[#1a1a1a] mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Portfolio Companies
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-[#ffd87a]">{company.name?.slice(0, 2).toUpperCase()}</span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-[#1a1a1a]">{company.name}</h1>
            <Badge className="text-[10px] border-0 bg-[#dae4c4] text-[#1a1a1a]">{company.stage?.replace(/_/g, ' ')}</Badge>
            <Badge className="text-[10px] border-0 bg-[#f5f5f5] text-[#4a4a4a]">{company.status}</Badge>
          </div>
          <p className="text-sm text-[#4a4a4a]">{company.sector || '—'} · {company.headquarters || '—'}</p>
        </div>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-6">
        <div className="p-4 bg-white">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Invested</p>
          <p className="text-lg font-bold text-[#1a1a1a]">{fmt(stats.total_invested)}</p>
        </div>
        <div className="p-4 bg-white">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Current Value</p>
          <p className="text-lg font-bold text-[#1a1a1a]">{fmt(stats.current_value)}</p>
        </div>
        <div className="p-4 bg-white">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">MOIC</p>
          <p className="text-lg font-bold text-[#1a1a1a]">{stats.multiple ? `${stats.multiple.toFixed(2)}×` : '—'}</p>
        </div>
        <div className="p-4 bg-white">
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Ownership</p>
          <p className="text-lg font-bold text-[#1a1a1a]">{stats.ownership_percentage ? `${stats.ownership_percentage.toFixed(2)}%` : '—'}</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="investments">Investments</TabsTrigger>
          <TabsTrigger value="valuations">Valuations</TabsTrigger>
          <TabsTrigger value="captable">Cap Table</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <Card className="bg-white border-[#e5e5e5]">
            <CardHeader>
              <CardTitle className="text-sm">Company Overview</CardTitle>
              <CardDescription>{company.description || 'No description on file.'}</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><p className="text-xs text-[#4a4a4a] uppercase mb-0.5">Founded</p><p>{company.founded_date || '—'}</p></div>
              <div><p className="text-xs text-[#4a4a4a] uppercase mb-0.5">Website</p><p>{company.website || '—'}</p></div>
              <div><p className="text-xs text-[#4a4a4a] uppercase mb-0.5">Current Valuation</p><p>{currentValuation ? fmt(currentValuation.post_money_valuation) : '—'}</p></div>
              {(company.founders || []).length > 0 && (
                <div className="col-span-full">
                  <p className="text-xs text-[#4a4a4a] uppercase mb-1">Founders</p>
                  <div className="flex flex-wrap gap-2">
                    {company.founders.map((f, i) => (
                      <Badge key={i} className="bg-[#f5f5f5] text-[#1a1a1a] border-0">{f.name}{f.title ? ` — ${f.title}` : ''}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investments */}
        <TabsContent value="investments" className="mt-4">
          <div className="border border-[#e5e5e5] overflow-hidden overflow-x-auto">
            {investments.length === 0 ? (
              <p className="text-sm text-[#4a4a4a] text-center py-10">No investment records</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1a1a1a]">
                    {['Round', 'Type', 'Security', 'Amount', 'Shares', 'Price/Sh', 'Terms'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {investments.map((inv, i) => (
                    <tr key={inv.id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{inv.round_name}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{INVESTMENT_TYPE_LABELS[inv.investment_type] || inv.investment_type}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{SECURITY_TYPE_LABELS[inv.security_type] || inv.security_type}</td>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{fmt(inv.investment_amount)}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{inv.shares_acquired?.toLocaleString() || '—'}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{inv.price_per_share ? fmt(inv.price_per_share) : '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {inv.board_seat && <span title="Board seat"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /></span>}
                          {inv.pro_rata_rights && <span title="Pro-rata rights"><TrendingUp className="w-3.5 h-3.5 text-[#c97a3a]" /></span>}
                          {inv.valuation_cap && <span className="text-[10px] text-[#4a4a4a]">Cap {fmt(inv.valuation_cap)}</span>}
                          {inv.discount_rate && <span className="text-[10px] text-[#4a4a4a]">{(inv.discount_rate * 100).toFixed(0)}% disc.</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Valuations */}
        <TabsContent value="valuations" className="mt-4">
          <div className="border border-[#e5e5e5] overflow-hidden overflow-x-auto">
            {valuations.length === 0 ? (
              <p className="text-sm text-[#4a4a4a] text-center py-10">No valuation history</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1a1a1a]">
                    {['Date', 'Method', 'Pre-Money', 'Post-Money', 'Share Price', 'FD Shares'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {valuations.map((v, i) => (
                    <tr key={v.id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{v.valuation_date}{v.is_current && <Badge className="ml-2 text-[9px] border-0 bg-[#ffd87a] text-[#1a1a1a]">Current</Badge>}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{v.valuation_method?.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{v.pre_money_valuation ? fmt(v.pre_money_valuation) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{v.post_money_valuation ? fmt(v.post_money_valuation) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{v.share_price ? fmt(v.share_price) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-[#4a4a4a]">{v.fully_diluted_shares?.toLocaleString() || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Cap Table */}
        <TabsContent value="captable" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#4a4a4a]">Snapshot:</span>
              {capTableHistory.length > 0 ? (
                <Select value={selectedSnapshotId || ''} onValueChange={setSelectedSnapshotId}>
                  <SelectTrigger className="w-[220px] h-8 text-xs bg-white">
                    <SelectValue placeholder="Select snapshot" />
                  </SelectTrigger>
                  <SelectContent>
                    {capTableHistory.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.snapshot_date}{s.is_current ? ' (current)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs text-[#4a4a4a]">No snapshots yet</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setWaterfallDialogOpen(true)} className="text-xs">
                <Waves className="w-3.5 h-3.5 mr-1.5" /> Run Waterfall
              </Button>
              <Button size="sm" onClick={openSnapshotDialog} className="text-xs">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Snapshot
              </Button>
            </div>
          </div>

          {!selectedSnapshot ? (
            <div className="text-center py-16 border border-[#e5e5e5] bg-white">
              <Users className="w-10 h-10 text-[#4a4a4a] mx-auto mb-3" />
              <p className="text-sm text-[#4a4a4a]">No cap table on file for this company yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-white border-[#e5e5e5] lg:col-span-1">
                <CardHeader><CardTitle className="text-sm">Ownership by Holder Type</CardTitle></CardHeader>
                <CardContent className="flex flex-col items-center gap-3">
                  <DonutChart data={donutData} />
                  <div className="w-full space-y-1">
                    {donutData.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                        <span className="flex-1 text-[#4a4a4a]">{d.name}</span>
                        <span className="font-medium text-[#1a1a1a]">{d.value.toLocaleString()} sh</span>
                      </div>
                    ))}
                  </div>
                  <div className="w-full pt-2 border-t border-[#e5e5e5] text-xs text-[#4a4a4a] flex justify-between">
                    <span>Total O/S</span><span className="font-medium text-[#1a1a1a]">{selectedSnapshot.total_shares_outstanding?.toLocaleString()}</span>
                  </div>
                  <div className="w-full text-xs text-[#4a4a4a] flex justify-between">
                    <span>Fully Diluted</span><span className="font-medium text-[#1a1a1a]">{selectedSnapshot.fully_diluted_shares?.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 border border-[#e5e5e5] overflow-hidden overflow-x-auto bg-white">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#1a1a1a]">
                      {['Holder', 'Type', 'Security', 'Shares', 'Own. %', 'FD %'].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e5e5]">
                    {entries.map((e, i) => (
                      <tr key={e.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                        <td className="px-4 py-3 text-sm text-[#1a1a1a]">{e.holder_name}</td>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{HOLDER_TYPE_LABELS[e.holder_type] || e.holder_type}</td>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{SECURITY_TYPE_LABELS[e.security_type] || e.security_type}</td>
                        <td className="px-4 py-3 text-sm text-[#1a1a1a]">{e.shares?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{e.ownership_percentage?.toFixed(2)}%</td>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{e.fully_diluted_percentage?.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* New Snapshot Dialog */}
      <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Cap Table Snapshot</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs text-[#4a4a4a]">Snapshot Date</label>
              <input type="date" value={snapshotForm.snapshot_date}
                onChange={e => setSnapshotForm(f => ({ ...f, snapshot_date: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
            <div>
              <label className="text-xs text-[#4a4a4a]">Total Shares O/S</label>
              <input type="number" value={snapshotForm.total_shares_outstanding}
                onChange={e => setSnapshotForm(f => ({ ...f, total_shares_outstanding: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
            <div>
              <label className="text-xs text-[#4a4a4a]">Fully Diluted Shares</label>
              <input type="number" value={snapshotForm.fully_diluted_shares}
                onChange={e => setSnapshotForm(f => ({ ...f, fully_diluted_shares: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-[#1a1a1a]">Holders</span>
            <Button variant="outline" size="sm" onClick={() => setSafeDialogOpen(true)} className="text-xs">
              Convert SAFEs into this snapshot
            </Button>
          </div>

          <div className="space-y-2">
            {snapshotForm.entries.map((row, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                <input placeholder="Holder name" value={row.holder_name}
                  onChange={e => updateEntryRow(idx, 'holder_name', e.target.value)}
                  className="col-span-3 px-2 py-1.5 text-xs border border-[#e5e5e5]" />
                <select value={row.holder_type} onChange={e => updateEntryRow(idx, 'holder_type', e.target.value)}
                  className="col-span-2 px-1 py-1.5 text-xs border border-[#e5e5e5]">
                  {Object.entries(HOLDER_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={row.security_type} onChange={e => updateEntryRow(idx, 'security_type', e.target.value)}
                  className="col-span-2 px-1 py-1.5 text-xs border border-[#e5e5e5]">
                  {Object.entries(SECURITY_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input type="number" placeholder="Shares" value={row.shares}
                  onChange={e => updateEntryRow(idx, 'shares', e.target.value)}
                  className="col-span-2 px-2 py-1.5 text-xs border border-[#e5e5e5]" />
                <input type="number" placeholder="Own. %" value={row.ownership_percentage}
                  onChange={e => updateEntryRow(idx, 'ownership_percentage', e.target.value)}
                  className="col-span-1 px-2 py-1.5 text-xs border border-[#e5e5e5]" />
                <input type="number" placeholder="FD %" value={row.fully_diluted_percentage}
                  onChange={e => updateEntryRow(idx, 'fully_diluted_percentage', e.target.value)}
                  className="col-span-1 px-2 py-1.5 text-xs border border-[#e5e5e5]" />
                <button onClick={() => removeEntryRow(idx)} className="col-span-1 text-[#c97a3a] hover:text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addEntryRow} className="mt-2 text-xs w-fit">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add holder
          </Button>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSnapshotDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitSnapshot} disabled={savingSnapshot}>
              {savingSnapshot ? 'Saving…' : 'Save Snapshot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert SAFEs Dialog */}
      <Dialog open={safeDialogOpen} onOpenChange={setSafeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Convert SAFEs / Notes</DialogTitle></DialogHeader>
          <p className="text-xs text-[#4a4a4a] mb-2">
            Enter the terms of the triggering priced round. Active SAFE and convertible-note
            investments will be converted to shares using the lower of cap price / discount price.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#4a4a4a]">Round Price / Share</label>
              <input type="number" value={safeForm.round_price_per_share}
                onChange={e => setSafeForm(f => ({ ...f, round_price_per_share: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
            <div>
              <label className="text-xs text-[#4a4a4a]">Pre-Money Valuation</label>
              <input type="number" value={safeForm.pre_money_valuation}
                onChange={e => setSafeForm(f => ({ ...f, pre_money_valuation: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
            <div>
              <label className="text-xs text-[#4a4a4a]">Fully Diluted Shares (pre-round)</label>
              <input type="number" value={safeForm.fully_diluted_shares_pre_round}
                onChange={e => setSafeForm(f => ({ ...f, fully_diluted_shares_pre_round: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSafeDialogOpen(false)}>Cancel</Button>
            <Button onClick={runConvertSafes} disabled={convertingSafes}>
              {convertingSafes ? 'Converting…' : 'Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waterfall Dialog */}
      <Dialog open={waterfallDialogOpen} onOpenChange={(o) => { setWaterfallDialogOpen(o); if (!o) setWaterfallResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Liquidation Waterfall</DialogTitle></DialogHeader>
          <p className="text-xs text-[#4a4a4a] mb-2">
            MVP model: preferred/SAFE/convertible holders are paid 1× non-participating preference pari passu;
            common/options share the residual pro-rata by fully-diluted %.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-[#4a4a4a]">Exit Value</label>
              <input type="number" value={exitValue} onChange={e => setExitValue(e.target.value)}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5]" />
            </div>
            <Button onClick={runWaterfall} disabled={runningWaterfall}>
              {runningWaterfall ? 'Running…' : 'Run'}
            </Button>
          </div>

          {waterfallResult && (
            <div className="mt-4">
              {waterfallResult.preference_shortfall && (
                <div className="mb-3 px-3 py-2 text-xs bg-[#fdf1e0] border border-[#e8a838] text-[#8a5a1a]">
                  Exit value is below the total preference stack ({fmt(waterfallResult.total_preference_stack)}) —
                  preference holders are paid pro-rata and residual holders receive $0.
                </div>
              )}
              <div className="border border-[#e5e5e5] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#1a1a1a]">
                      {['Holder', 'Security', 'Preference', 'Payout'].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-[#ffd87a] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e5e5]">
                    {waterfallResult.payouts.map((p, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                        <td className="px-3 py-2 text-xs text-[#1a1a1a]">{p.holder_name}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{SECURITY_TYPE_LABELS[p.security_type] || p.security_type}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{p.preference_amount ? fmt(p.preference_amount) : '—'}</td>
                        <td className="px-3 py-2 text-xs font-medium text-[#1a1a1a]">{fmt(p.payout)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
